import React from 'react';
import { useNavigate } from 'react-router-dom';
import { patientAPI } from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Pill, FlaskConical, Stethoscope, StickyNote, Share2, UserCog, Trash2, DoorOpen, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { AddNoteDialog, ReferPatientDialog, AssignPatientDialog, DeletePatientDialog } from './PatientActionDialogs';
import { apiErrorMessage } from '../../../lib/utils';

const ActionCard = ({ icon: Icon, title, description, iconClass = 'text-red-500', className, children }) => (
  <Card className={className}>
    <CardHeader>
      <CardTitle className="text-lg font-semibold font-[Manrope] flex items-center gap-2">
        <Icon className={`w-5 h-5 ${iconClass}`} />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>
      {description && <p className="text-sm text-slate-600 mb-4">{description}</p>}
      {children}
    </CardContent>
  </Card>
);

export const PatientActionsTab = ({ patient, doctors, nurses, permissions, onRefresh, onForceClose }) => {
  const navigate = useNavigate();
  const id = patient.id;
  const { canPrescribe, canOrderTests, canAddNotes, canRefer, canForceClose, canAssign, canDelete, canCloseAndDischarge, isReceptionist } = permissions;

  const handleCloseCase = async () => {
    try {
      await patientAPI.refer(id, {
        refer_to_type: 'department',
        refer_to_id: 'Reception',
        reason: 'Case closed - Ready for checkout and billing',
        urgency: 'normal'
      });
      toast.success('Case closed successfully. Patient referred to Reception for checkout.');
      navigate('/patients');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to close case'));
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="patient-actions-tab">
      {canPrescribe && (
        <ActionCard icon={Pill} title="Prescribe Medication" description="Create a new prescription for this patient">
          <Button className="w-full bg-red-500 hover:bg-red-600" onClick={() => navigate(`/prescriptions?patient_id=${id}`)} data-testid="new-prescription-btn">
            <Pill className="w-4 h-4 mr-2" />New Prescription
          </Button>
        </ActionCard>
      )}

      {canOrderTests && (
        <ActionCard icon={FlaskConical} title="Order Lab Test" description="Order laboratory or imaging tests">
          <div className="flex gap-2">
            <Button className="flex-1 bg-cyan-500 hover:bg-cyan-600" onClick={() => navigate(`/lab-tests?patient_id=${id}`)} data-testid="order-lab-test-btn">
              <FlaskConical className="w-4 h-4 mr-2" />Lab Test
            </Button>
            <Button className="flex-1 bg-blue-500 hover:bg-blue-600" onClick={() => navigate(`/imaging-tests?patient_id=${id}`)} data-testid="order-imaging-btn">
              Imaging
            </Button>
          </div>
        </ActionCard>
      )}

      {canPrescribe && (
        <ActionCard icon={Stethoscope} title="Schedule Procedure" description="Schedule a medical procedure for this patient">
          <Button className="w-full bg-violet-500 hover:bg-violet-600" onClick={() => navigate(`/procedures?patient_id=${id}`)} data-testid="new-procedure-btn">
            <Stethoscope className="w-4 h-4 mr-2" />New Procedure
          </Button>
        </ActionCard>
      )}

      {canAddNotes && (
        <ActionCard icon={StickyNote} title="Add Note / Observation">
          <AddNoteDialog patientId={id} onSuccess={onRefresh} />
        </ActionCard>
      )}

      {canRefer && (
        <ActionCard icon={Share2} title="Refer Patient">
          <ReferPatientDialog patientId={id} doctors={doctors} excludeDoctorId={patient.assigned_doctor_id} onSuccess={onRefresh} />
        </ActionCard>
      )}

      {canPrescribe && (
        <ActionCard icon={DoorOpen} title="Close Case" description="Treatment complete? Send patient to Reception for checkout and billing.">
          <Button className="w-full bg-emerald-500 hover:bg-emerald-600" onClick={handleCloseCase} data-testid="close-case-btn">
            <DoorOpen className="w-4 h-4 mr-2" />Close Case & Send to Reception
          </Button>
        </ActionCard>
      )}

      {canForceClose && (
        <ActionCard icon={ShieldAlert} iconClass="text-amber-600" className="border-amber-200 bg-amber-50/30" title="Force Close / Opt-Out" description="Patient declined pending services? Close case even with pending tasks.">
          <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white" onClick={onForceClose} data-testid="force-close-btn">
            <ShieldAlert className="w-4 h-4 mr-2" />Force Close / Patient Opt-Out
          </Button>
        </ActionCard>
      )}

      {canAssign && (
        <ActionCard icon={UserCog} title="Assign / Reassign">
          <AssignPatientDialog patient={patient} doctors={doctors} nurses={nurses} onSuccess={onRefresh} />
        </ActionCard>
      )}

      {canCloseAndDischarge && (
        <ActionCard icon={DoorOpen} title="Close & Discharge" description="Process patient discharge and billing">
          <Button className="w-full bg-blue-500 hover:bg-blue-600" onClick={() => navigate('/workflow/reception-closure')} data-testid="go-to-closure-btn">
            <DoorOpen className="w-4 h-4 mr-2" />Go to Case Closure
          </Button>
        </ActionCard>
      )}

      {canDelete && (
        <ActionCard icon={Trash2} title="Delete Patient">
          <DeletePatientDialog patientId={id} isReceptionist={isReceptionist} onDeleted={() => navigate('/patients')} />
        </ActionCard>
      )}
    </div>
  );
};
